<template>
  <div class="workflow-history-page">
    <div class="page-header">
      <div class="header-left">
        <h1>Workflow Execution History</h1>
        <router-link to="/admin/scout/workflow/running" class="nav-link">
          <i class="bi bi-play-circle"></i>
          View Running Workflows
        </router-link>
      </div>
      <div class="header-actions">
        <div class="period-filter-bar">
          <button
            v-for="period in periods"
            :key="period.value"
            @click="changePeriod(period.value)"
            :class="['period-tab', { active: selectedPeriod === period.value }]"
          >
            {{ period.label }}
          </button>
        </div>
        <button @click="loadHistoryFresh" class="btn-primary" :disabled="loading">
          <span v-if="loading">↻</span>
          <span v-else>Refresh</span>
        </button>
      </div>
    </div>

    <!-- Statistics Overview -->
    <div class="stats-overview">
      <WorkflowStatistics
        :selectedPeriod="selectedPeriod"
        :periods="periods.map(function(p) { return p.value; })"
        :showPeriodSelector="false"
        chartTitle="Execution Trends"
      />
    </div>

    <div class="filters-section">
      <div class="filter-group">
        <label for="status-filter">Status:</label>
        <select 
          id="status-filter" 
          v-model="statusFilter" 
          @change="handleFilterChange"
          class="filter-select"
        >
          <option value="">All</option>
          <option value="completed">Completed</option>
          <option value="errored">Failed</option>
          <option value="timeout">Timeout</option>
          <option value="killed">Killed</option>
        </select>
      </div>

      <div class="filter-group">
        <label for="type-filter">Type:</label>
        <select 
          id="type-filter" 
          v-model="typeFilter" 
          @change="handleFilterChange"
          class="filter-select"
        >
          <option value="">All</option>
          <option value="project">Project</option>
          <option value="component">Component</option>
        </select>
      </div>

      <div class="filter-group">
        <label for="workflow-name-filter">Workflow Name:</label>
        <input 
          id="workflow-name-filter"
          type="text"
          v-model="workflowNameFilter"
          @input="handleFilterChange"
          placeholder="Search by name..."
          class="filter-input"
        />
      </div>

      <div class="filter-group">
        <label for="ref-filter">Ref:</label>
        <input 
          id="ref-filter"
          type="text"
          v-model="refFilter"
          @input="handleFilterChange"
          placeholder="Search by ref..."
          class="filter-input"
        />
      </div>

      <div class="filter-group">
        <label for="context-filter">Context UUID:</label>
        <input 
          id="context-filter"
          type="text"
          v-model="contextFilter"
          @input="handleFilterChange"
          placeholder="Search by context UUID..."
          class="filter-input"
        />
      </div>
      
      <div class="filter-group">
        <label for="page-size">Per Page:</label>
        <select 
          id="page-size" 
          v-model.number="pageSize" 
          @change="handlePageSizeChange"
          class="filter-select"
        >
          <option :value="10">10</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </div>
    </div>

    <div v-if="error" class="error-message">
      <span>⚠️ {{ error }}</span>
      <button @click="loadHistoryFresh" class="btn-text">Try Again</button>
    </div>

    <div v-if="loading && !history.length" class="loading-state">
      <div class="spinner"></div>
      <p>Loading workflow history...</p>
    </div>

    <div v-else-if="!loading && !history.length" class="empty-state">
      <div class="empty-icon">
        <i class="bi bi-inbox"></i>
      </div>
      <h3>No workflow executions found</h3>
      <p>There are no workflow executions matching your filters.</p>
    </div>

    <div v-else class="history-table-container">
      <table class="history-table">
        <thead>
          <tr>
            <th class="col-execution-id">Execution UUID</th>
            <th class="col-workflow">Workflow</th>
            <th class="col-type">Type</th>
            <th class="col-ref">Ref</th>
            <th class="col-status">Status</th>
            <th class="col-env">Environment</th>
            <th class="col-started">Started</th>
            <th class="col-duration">Duration</th>
            <th class="col-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          <!-- Loading indicator -->
          <tr v-if="loading" class="loading-row">
            <td colspan="9" class="loading-cell">
              <div class="table-loading">
                <div class="spinner"></div>
                <span>Loading...</span>
              </div>
            </td>
          </tr>
          <template v-else v-for="group in groupedHistory">
            <!-- Context Group Header -->
            <tr :key="`context-${group.contextId}`" class="context-group-header">
              <td colspan="9" class="context-header-cell">
                <div class="context-header-content">
                  <i class="bi bi-folder2-open"></i>
                  <span v-if="group.contextId !== 'no-context'" class="context-label">
                    Context: <code>{{ truncateUuid(group.contextId, 16) }}</code>
                  </span>
                  <span v-else class="context-label">No Context</span>
                  <CopyButton v-if="group.contextId !== 'no-context'" :text="group.contextId" />
                  <span class="execution-count">{{ group.executions.length }} execution{{ group.executions.length > 1 ? 's' : '' }}</span>
                </div>
              </td>
            </tr>
            <!-- Executions in this context -->
            <tr 
              v-for="execution in group.executions" 
              :key="execution.execution_uuid"
              class="history-row"
              @click="viewDetails(execution.execution_uuid)"
            >
            <td class="execution-id">
              <span class="uuid-text">{{ truncateUuid(execution.execution_uuid, 12) }}</span>
              <CopyButton :text="execution.execution_uuid" />
            </td>
            <td class="workflow-name">{{ execution.workflow_name }}</td>
            <td>
              <span class="type-badge" :class="execution.type">
                {{ execution.type || 'N/A' }}
              </span>
            </td>
            <td>
              <code v-if="execution.ref" class="ref-cell">{{ execution.ref }}</code>
              <span v-else class="text-muted">N/A</span>
            </td>
            <td>
              <StatusBadge :status="execution.status" size="small" />
            </td>
            <td class="env-badge">
              <span :class="`env-${execution.env}`">{{ execution.env.toUpperCase() }}</span>
            </td>
            <td class="timestamp">{{ formatRelativeTime(execution.start_time) }}</td>
            <td class="duration">{{ formatDurationShort(execution.duration) }}</td>
            <td class="actions">
              <div class="actions-container">
                <button 
                  @click.stop="openQuickPeek(execution.execution_uuid)" 
                  class="btn-action"
                  title="Quick Peek"
                >
                  <i class="bi bi-eye"></i>
                </button>
                
                <div v-if="execution.status === 'errored' || execution.status === 'killed' || execution.status === 'completed'" class="dropdown" @click.stop>
                  <button 
                    @click="toggleDropdown(execution.execution_uuid)"
                    class="btn-action dropdown-toggle no-caret"
                    title="More Actions"
                  >
                    <i class="bi bi-three-dots-vertical"></i>
                  </button>
                  
                  <div v-show="openDropdown === execution.execution_uuid" class="dropdown-menu">
                    <button 
                      v-if="execution.status === 'errored' || execution.status === 'killed'"
                      @click="confirmRestartSmart(execution.execution_uuid, execution.workflow_name)" 
                      class="dropdown-item restart-smart"
                    >
                      <i class="bi bi-arrow-clockwise"></i>
                      Smart Restart
                    </button>
                    <button 
                      @click="confirmRestartFresh(execution.execution_uuid, execution.workflow_name)" 
                      class="dropdown-item restart-fresh"
                    >
                      <i class="bi bi-arrow-repeat"></i>
                      Fresh Restart
                    </button>
                  </div>
                </div>
              </div>
            </td>
          </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div v-if="history.length > 0" class="pagination">
      <button 
        @click="prevPage" 
        :disabled="currentPage === 0 || loading"
        class="btn-pagination"
      >
        ← Previous
      </button>
      <span class="pagination-info">
        Page {{ currentPage + 1 }}<span v-if="totalPages > 0"> of {{ totalPages }}</span>
        <span class="total-records">({{ totalRecords }} total)</span>
      </span>
      <button 
        @click="nextPage" 
        :disabled="!hasMore || loading"
        class="btn-pagination"
      >
        Next →
      </button>
    </div>

    <!-- Quick Peek Modal -->
    <WorkflowQuickPeekModal
      :show="quickPeekModal.show"
      :executionUuid="quickPeekModal.executionUuid"
      :workflowHistoryService="workflowHistoryService"
      @close="closeQuickPeek"
    />

    <!-- Confirmation Modal -->
    <ConfirmModal
      :show="confirmModal.show"
      :title="confirmModal.title"
      :message="confirmModal.message"
      :confirmText="confirmModal.confirmText"
      :dangerButton="confirmModal.danger"
      :icon="confirmModal.icon"
      @confirm="handleConfirmAction"
      @cancel="closeConfirmModal"
    />
  </div>
</template>

<script>
import WorkflowHistory from '@/lib/WorkflowHistory';
import StatusBadge from '@/components/workflow/StatusBadge.vue';
import CopyButton from '@/components/workflow/CopyButton.vue';
import ConfirmModal from '@/components/workflow/ConfirmModal.vue';
import WorkflowStatistics from '@/components/workflow/WorkflowStatistics.vue';
import WorkflowQuickPeekModal from '@/components/workflow/WorkflowQuickPeekModal.vue';
import { 
  formatDurationShort, 
  formatRelativeTime, 
  truncateUuid,
  formatDuration
} from '@/shared/workflowHistoryUtils';

// Module-level cache — survives unmount/remount (back-navigation)
const _historyCache = new Map()
const _HISTORY_CACHE_TTL = 30 * 1000 // 30 seconds
let _savedScrollY = 0

function _historyCacheKey(params) {
  return JSON.stringify(params)
}

export default {
  name: 'WorkflowHistoryList',
  components: {
    StatusBadge,
    CopyButton,
    ConfirmModal,
    WorkflowStatistics,
    WorkflowQuickPeekModal
  },
  data() {
    return {
      history: [],
      totalRecords: 0,
      loading: false,
      error: null,
      currentPage: 0,
      pageSize: 20,
      statusFilter: '',
      typeFilter: '',
      workflowNameFilter: '',
      refFilter: '',
      contextFilter: '',
      hasMore: true,
      workflowHistoryService: null,
      selectedPeriod: 1,
      periods: [
        { value: 1, label: '24h' },
        { value: 3, label: '3D' },
        { value: 7, label: '7D' },
        { value: 14, label: '14D' },
        { value: 30, label: '30D' },
        { value: 90, label: '90D' },
      ],
      filterDebounceTimer: null,
      openDropdown: null,
      quickPeekModal: {
        show: false,
        executionUuid: null
      },
      confirmModal: {
        show: false,
        title: '',
        message: '',
        confirmText: 'Confirm',
        danger: false,
        icon: 'question',
        action: null,
        params: null
      }
    };
  },
  created() {
    this.workflowHistoryService = new WorkflowHistory(this.$workflowApi);
    this.restoreFromUrl();
    this.loadHistory();
    
    // Close dropdown when clicking outside
    document.addEventListener('click', this.closeDropdownOnClickOutside);
  },
  beforeDestroy() {
    // Clean up event listener
    document.removeEventListener('click', this.closeDropdownOnClickOutside);
  },
  methods: {
    formatDurationShort,
    formatRelativeTime,
    truncateUuid,
    formatDuration,
    
    async loadHistory() {
      this.loading = true;
      this.error = null;
      
      try {
        const params = {
          limit: this.pageSize,
          offset: this.currentPage * this.pageSize
        };
        
        if (this.statusFilter) {
          params.status = this.statusFilter;
        }
        
        if (this.typeFilter) {
          params.type = this.typeFilter;
        }
        
        if (this.workflowNameFilter) {
          params.workflow_name = this.workflowNameFilter;
        }
        
        if (this.refFilter) {
          params.ref = this.refFilter;
        }

        if (this.contextFilter) {
          params.context_uuid = this.contextFilter;
        }

        if (this.selectedPeriod) {
          const dateFrom = new Date();
          dateFrom.setDate(dateFrom.getDate() - this.selectedPeriod);
          dateFrom.setHours(0, 0, 0, 0);
          params.date_from = dateFrom.toISOString();
        }

        // Return cached result if still fresh
        const cacheKey = _historyCacheKey(params)
        const cached = _historyCache.get(cacheKey)
        if (cached && (Date.now() - cached.ts) < _HISTORY_CACHE_TTL) {
          this.history = cached.groups
          this.totalRecords = cached.total
          this.hasMore = cached.hasMore
          this._restoreScroll()
          return
        }
        
        const result = await this.workflowHistoryService.fetchHistory(params);
        this.history = result.groups;
        this.totalRecords = result.total;
        this.hasMore = (this.currentPage + 1) * this.pageSize < result.total;

        _historyCache.set(cacheKey, { groups: result.groups, total: result.total, hasMore: this.hasMore, ts: Date.now() })

        this.updateUrl();
        this._restoreScroll();
      } catch (err) {
        this.error = err.message || 'Failed to load workflow history';
        console.error('Error loading history:', err);
      } finally {
        this.loading = false;
      }
    },

    async loadHistoryFresh() {
      _historyCache.clear()
      await this.loadHistory()
    },
    
    changePeriod(days) {
      this.selectedPeriod = days;
      this.currentPage = 0;
      this.updateUrl();
      this.loadHistoryFresh();
    },

    handleFilterChange() {
      // Debounce text input filters
      if (this.filterDebounceTimer) {
        clearTimeout(this.filterDebounceTimer);
      }
      // Show loading immediately so stale cached results don't stay visible during debounce
      this.loading = true;
      
      this.filterDebounceTimer = setTimeout(() => {
        this.currentPage = 0;
        this.updateUrl();
        this.loadHistoryFresh();
      }, 500);
    },
    
    handlePageSizeChange() {
      this.currentPage = 0;
      this.updateUrl();
      this.loadHistoryFresh();
    },
    
    nextPage() {
      if (this.hasMore && !this.loading) {
        this.currentPage++;
        this.updateUrl();
        this.loadHistory();
      }
    },
    
    prevPage() {
      if (this.currentPage > 0 && !this.loading) {
        this.currentPage--;
        this.updateUrl();
        this.loadHistory();
      }
    },
    
    viewDetails(executionUuid) {
      _savedScrollY = window.scrollY;
      this.$router.push(`/admin/scout/workflow/history/${executionUuid}`);
    },

    _restoreScroll() {
      if (_savedScrollY > 0) {
        var scrollY = _savedScrollY;
        _savedScrollY = 0;
        this.$nextTick(function() {
          window.scrollTo(0, scrollY);
        });
      }
    },

    openQuickPeek(executionUuid) {
      this.quickPeekModal = {
        show: true,
        executionUuid
      };
    },

    closeQuickPeek() {
      this.quickPeekModal = {
        show: false,
        executionUuid: null
      };
    },

    restoreFromUrl() {
      const query = this.$route.query;
      if (query.page !== undefined) {
        const page = parseInt(query.page, 10);
        if (!isNaN(page) && page >= 0) this.currentPage = page;
      }
      if (query.pageSize !== undefined) {
        const size = parseInt(query.pageSize, 10);
        if ([10, 20, 50, 100].includes(size)) this.pageSize = size;
      }
      if (query.status) this.statusFilter = query.status;
      if (query.type) this.typeFilter = query.type;
      if (query.workflow_name) this.workflowNameFilter = query.workflow_name;
      if (query.ref) this.refFilter = query.ref;
      if (query.context_uuid) this.contextFilter = query.context_uuid;
      if (query.period !== undefined) {
        const p = parseInt(query.period, 10);
        if (!isNaN(p) && p > 0) this.selectedPeriod = p;
      }
    },

    updateUrl() {
      const query = {};
      if (this.currentPage > 0) query.page = String(this.currentPage);
      if (this.pageSize !== 20) query.pageSize = String(this.pageSize);
      if (this.statusFilter) query.status = this.statusFilter;
      if (this.typeFilter) query.type = this.typeFilter;
      if (this.workflowNameFilter) query.workflow_name = this.workflowNameFilter;
      if (this.refFilter) query.ref = this.refFilter;
      if (this.contextFilter) query.context_uuid = this.contextFilter;
      if (this.selectedPeriod !== 1) query.period = String(this.selectedPeriod);

      // Only update if query actually changed (avoid duplicate navigation)
      const currentQuery = this.$route.query;
      const changed = Object.keys(query).length !== Object.keys(currentQuery).length ||
        Object.keys(query).some(k => query[k] !== currentQuery[k]);
      if (changed) {
        this.$router.replace({ query }).catch(() => {});
      }
    },

    toggleDropdown(executionUuid) {
      if (this.openDropdown === executionUuid) {
        this.openDropdown = null;
      } else {
        this.openDropdown = executionUuid;
      }
    },

    closeDropdownOnClickOutside(event) {
      // Check if click is outside dropdown
      const dropdown = event.target.closest('.dropdown');
      if (!dropdown && this.openDropdown) {
        this.openDropdown = null;
      }
    },

    confirmRestartSmart(executionUuid, workflowName) {
      this.openDropdown = null;
      this.confirmModal = {
        show: true,
        title: 'Smart Restart Workflow',
        message: `Are you sure you want to restart "${workflowName}" with smart mode? This will preserve completed nodes and only retry failed/pending nodes.`,
        confirmText: 'Smart Restart',
        danger: false,
        icon: 'info',
        action: 'restartSmart',
        params: { executionUuid }
      };
    },

    confirmRestartFresh(executionUuid, workflowName) {
      this.openDropdown = null;
      this.confirmModal = {
        show: true,
        title: 'Fresh Restart Workflow',
        message: `Are you sure you want to restart "${workflowName}" with fresh mode? This will clear all node states and restart the entire workflow from scratch.`,
        confirmText: 'Fresh Restart',
        danger: false,
        icon: 'warning',
        action: 'restartFresh',
        params: { executionUuid }
      };
    },

    async restartWorkflowSmart(executionUuid) {
      try {
        const response = await this.workflowHistoryService.restartWorkflowSmart(executionUuid);
        console.log('Smart restart response:', response);
        
        // Show toast with action button
        const newExecutionUuid = response && response.execution_uuid ? response.execution_uuid : null;
        console.log('New execution UUID:', newExecutionUuid);
        
        if (newExecutionUuid) {
          const h = this.$createElement;
          const message = h('div', [
            h('p', { class: 'mb-2' }, 'Workflow execution started (smart mode)'),
            h('b-button', {
              props: { size: 'sm', variant: 'light' },
              on: {
                click: () => {
                  this.$router.push(`/admin/scout/workflow/running/${newExecutionUuid}`);
                  this.$bvToast.hide('workflow-restart-smart');
                }
              }
            }, 'See execution details')
          ]);
          this.$bvToast.toast([message], {
            id: 'workflow-restart-smart',
            title: 'Success',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000
          });
        } else {
          this.$bvToast.toast('Workflow restarted successfully (smart mode)', {
            title: 'Success',
            variant: 'success',
            solid: true
          });
        }
        
        // Restart creates a NEW execution — original row is unchanged, no reload needed
      } catch (error) {
        const errorMessage = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : 'Failed to restart workflow';
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error restarting workflow (smart):', error);
      }
    },

    async restartWorkflowFresh(executionUuid) {
      try {
        const response = await this.workflowHistoryService.restartWorkflowFresh(executionUuid);
        console.log('Fresh restart response:', response);
        
        const newExecutionUuid = response && response.execution_uuid ? response.execution_uuid : null;
        console.log('New execution UUID:', newExecutionUuid);
        
        if (newExecutionUuid) {
          const h = this.$createElement;
          const message = h('div', [
            h('p', { class: 'mb-2' }, 'Workflow execution started (fresh mode)'),
            h('b-button', {
              props: { size: 'sm', variant: 'light' },
              on: {
                click: () => {
                  this.$router.push(`/admin/scout/workflow/running/${newExecutionUuid}`);
                  this.$bvToast.hide('workflow-restart-fresh');
                }
              }
            }, 'See execution details')
          ]);
          this.$bvToast.toast([message], {
            id: 'workflow-restart-fresh',
            title: 'Success',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000
          });
        } else {
          this.$bvToast.toast('Workflow restarted successfully (fresh mode)', {
            title: 'Success',
            variant: 'success',
            solid: true
          });
        }

        // Restart creates a NEW execution — original row is unchanged, no reload needed
      } catch (error) {
        const errorMessage = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : 'Failed to restart workflow';
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error restarting workflow (fresh):', error);
      }
    },

    handleConfirmAction() {
      const { action, params } = this.confirmModal;
      if (action === 'restartSmart') {
        this.restartWorkflowSmart(params.executionUuid);
      } else if (action === 'restartFresh') {
        this.restartWorkflowFresh(params.executionUuid);
      }
      this.closeConfirmModal();
    },

    closeConfirmModal() {
      this.confirmModal = {
        show: false,
        title: '',
        message: '',
        confirmText: 'Confirm',
        danger: false,
        icon: 'question',
        action: null,
        params: null
      };
    }
  },
  computed: {
    groupedHistory() {
      // API already returns groups: [{ context_uuid, executions }]
      return this.history.map(group => ({
        contextId: group.context_uuid || 'no-context',
        executions: (group.executions || []).slice().sort((a, b) => new Date(b.start_time) - new Date(a.start_time))
      }));
    },
    totalPages() {
      return Math.ceil(this.totalRecords / this.pageSize);
    }
  }
};
</script>

<style scoped>
.workflow-history-page {
  padding: 24px;
  background-color: var(--bg-card);
  min-height: 100vh;
  color: var(--text-primary);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.header-left h1 {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  color: var(--text-primary);
}

.nav-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: var(--form-bg);
  border: 1px solid var(--border-default);
  border-radius: 6px;
  color: var(--primary-start);
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
}

.nav-link:hover {
  background-color: var(--bg-card);
  border-color: var(--primary-start);
}

.page-header h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
}

.header-actions {
  display: flex;
  gap: 12px;
}

.btn-primary,
.btn-secondary {
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  font-size: 14px;
}

.btn-primary {
  background-color: var(--primary-start);
  color: var(--text-primary);
  border: 1px solid var(--primary-start);
}

.btn-primary:hover:not(:disabled) {
  background-color: hsl(243, 91%, 64%);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: var(--bg-card);
  color: var(--text-primary);
  border: 1px solid var(--border-default);
}

.btn-secondary:hover {
  background-color: var(--bg-card);
}

.filters-section {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
  padding: 16px;
  background-color: var(--form-bg);
  border-radius: 8px;
  border: 1px solid var(--border-default);
  flex-wrap: wrap;
}

.period-filter-bar {
  display: flex;
  gap: 4px;
}

.period-tab {
  padding: 6px 16px;
  border-radius: 6px;
  border: 1px solid var(--border-default);
  background-color: var(--form-bg);
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.period-tab:hover {
  border-color: var(--primary-start);
  color: var(--primary-start);
}

.period-tab.active {
  background-color: var(--primary-start);
  border-color: var(--primary-start);
  color: #fff;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-group label {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
  white-space: nowrap;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid var(--border-default);
  border-radius: 6px;
  background-color: var(--bg-card);
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  min-width: 120px;
}

.filter-input {
  padding: 8px 12px;
  border: 1px solid var(--border-default);
  border-radius: 6px;
  background-color: var(--bg-card);
  color: var(--text-primary);
  font-size: 14px;
  min-width: 200px;
}

.filter-input::placeholder {
  color: #6b7280;
}

.filter-input:focus,
.filter-select:focus {
  outline: none;
  border-color: var(--primary-start);
  box-shadow: 0 0 0 3px rgba(108, 99, 255, 0.2);
}

.error-message {
  background-color: var(--form-bg);
  border: 1px solid #ef4444;
  color: #fca5a5;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.btn-text {
  background: transparent;
  border: none;
  color: #fca5a5;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 600;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--text-primary);
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid var(--border-default);
  border-top-color: var(--primary-start);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-state h3 {
  margin: 0 0 8px 0;
  color: var(--text-primary);
}

.empty-state p {
  color: #9ca3af;
  margin: 0;
}

.history-table-container {
  background: var(--bg-card);
  border-radius: 8px;
  overflow: visible;
  border: 1px solid var(--border-default);
}

.history-table {
  width: 100%;
  border-collapse: collapse;
}

.history-table thead {
  background-color: var(--form-bg);
  border-bottom: 2px solid var(--border-default);
}

.history-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.history-table th:nth-child(1),
.history-table th:nth-child(2) {
  min-width: 200px;
  white-space: nowrap;
}

.history-table tbody tr {
  border-bottom: 1px solid var(--border-default);
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.history-table tbody tr:hover {
  background-color: var(--form-bg);
}

.context-group-header {
  background-color: var(--bg-card) !important;
  border-top: 2px solid var(--primary-start);
  cursor: default !important;
}

.context-group-header:hover {
  background-color: var(--bg-card) !important;
}

.context-header-cell {
  padding: 12px 16px !important;
}

.context-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  color: var(--primary-start);
}

.context-header-content i {
  font-size: 16px;
}

.context-label {
  color: var(--text-primary);
  font-size: 14px;
}

.context-label code {
  background-color: var(--form-bg);
  padding: 4px 8px;
  border-radius: 4px;
  color: var(--primary-start);
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 12px;
}

.execution-count {
  margin-left: auto;
  color: #9ca3af;
  font-size: 13px;
  font-weight: 400;
}

.loading-row {
  background-color: transparent !important;
  cursor: default !important;
}

.loading-row:hover {
  background-color: transparent !important;
}

.loading-cell {
  padding: 32px !important;
  text-align: center;
}

.table-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #9ca3af;
  font-size: 14px;
}

.table-loading .spinner {
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-default);
  border-top-color: var(--primary-start);
}

.history-table td {
  padding: 16px;
  color: var(--text-primary);
  font-size: 14px;
}

.workflow-name {
  font-weight: 600;
  color: var(--text-primary);
}

.execution-id {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 13px;
  color: #9ca3af;
  white-space: nowrap;
}

.context-id {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 13px;
  color: #9ca3af;
  white-space: nowrap;
}

.duration {
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  color: #9ca3af;
}

.env-badge span {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.env-dev {
  background-color: var(--border-default);
  color: #fbbf24;
  border: 1px solid #fbbf24;
}

.env-stage {
  background-color: var(--border-default);
  color: #fb923c;
  border: 1px solid #fb923c;
}

.env-live {
  background-color: var(--border-default);
  color: #10b981;
  border: 1px solid #10b981;
}

.timestamp {
  color: #9ca3af;
  font-size: 13px;
}

.actions {
  text-align: center;
}

.actions-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.dropdown {
  position: relative;
  display: inline-block;
}

.dropdown-toggle {
  padding: 4px 8px !important;
  position: relative;
  z-index: 1;
}

.dropdown-toggle.no-caret::after {
  display: none;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: var(--form-bg);
  border: 1px solid var(--border-default);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  min-width: 180px;
  overflow: hidden;
  display: block;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  font-size: 14px;
  text-align: left;
  transition: all 0.2s ease;
  border-bottom: 1px solid var(--border-default);
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background-color: var(--bg-card);
}

.dropdown-item i {
  font-size: 16px;
}

.dropdown-item.restart-smart {
  color: #10b981;
}

.dropdown-item.restart-smart:hover {
  background-color: rgba(16, 185, 129, 0.1);
}

.dropdown-item.restart-fresh {
  color: #f59e0b;
}

.dropdown-item.restart-fresh:hover {
  background-color: rgba(245, 158, 11, 0.1);
}

.btn-action {
  background: transparent;
  border: 1px solid var(--border-default);
  border-radius: 4px;
  padding: 4px 12px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s ease;
  color: var(--text-primary);
  margin-left: 4px;
}

.btn-action:hover {
  background-color: var(--form-bg);
  border-color: var(--border-hover);
}

.btn-action.restart-smart {
  color: #10b981;
  border-color: #10b981;
}

.btn-action.restart-smart:hover {
  background-color: rgba(16, 185, 129, 0.1);
  border-color: #10b981;
}

.btn-action.restart-fresh {
  color: #f59e0b;
  border-color: #f59e0b;
}

.btn-action.restart-fresh:hover {
  background-color: rgba(245, 158, 11, 0.1);
  border-color: #f59e0b;
}

.ref-cell {
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.85rem;
  color: #a0aec0;
  background-color: rgba(108, 99, 255, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}

.type-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: capitalize;
}

.type-badge.project {
  background-color: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.type-badge.component {
  background-color: rgba(139, 92, 246, 0.2);
  color: #a78bfa;
  border: 1px solid rgba(139, 92, 246, 0.3);
}

.text-muted {
  color: #6b7280;
  font-style: italic;
  font-size: 0.9rem;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  padding: 16px;
}

.btn-pagination {
  padding: 8px 16px;
  background-color: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--text-primary);
}

.btn-pagination:hover:not(:disabled) {
  background-color: var(--bg-card);
  border-color: var(--border-hover);
}

.btn-pagination:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  color: #9ca3af;
  font-weight: 600;
}

.total-records {
  margin-left: 4px;
  font-weight: 400;
  color: #6b7280;
}

/* Stats Overview */
.stats-overview {
  margin-bottom: 32px;
}

.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.stats-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
}

.period-selector {
  display: flex;
  gap: 8px;
  background-color: var(--form-bg);
  padding: 4px;
  border-radius: 8px;
  border: 1px solid var(--border-default);
}

.period-btn {
  padding: 8px 16px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #9ca3af;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
}

.period-btn:hover {
  background-color: var(--border-default);
  color: var(--text-primary);
}

.period-btn.active {
  background-color: var(--primary-start);
  color: var(--text-primary);
}

.stats-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  margin-bottom: 24px;
}
</style>
